import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Global keyboard shortcuts component for accessibility
 * Implements keyboard navigation as defined in AccessibilitySettings
 */
const KeyboardShortcuts: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if keyboard shortcuts are enabled
    const getKeyboardShortcutsEnabled = (): boolean => {
      const prefs = localStorage.getItem('accessibilityPreferences');
      if (!prefs) return true; // Default to enabled
      try {
        const parsed = JSON.parse(prefs);
        return parsed.keyboardShortcuts !== false;
      } catch {
        return true;
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Check if shortcuts are enabled
      if (!getKeyboardShortcutsEnabled()) return;

      // Alt + A: Navigate to Appointments
      if (event.altKey && event.key.toLowerCase() === 'a' && !isInputField) {
        event.preventDefault();

        // Determine dashboard based on current route
        if (location.pathname.includes('client-dashboard')) {
          // Trigger appointment section in client dashboard
          window.dispatchEvent(new CustomEvent('navigate-to-section', {
            detail: { section: 'overview' } // Client appointments are in overview
          }));
        } else if (location.pathname.includes('provider-dashboard')) {
          // Navigate to meetings/appointments tab in provider dashboard
          window.dispatchEvent(new CustomEvent('navigate-to-tab', {
            detail: { tab: 'meetings' }
          }));
        } else {
          // If not on a dashboard, try to navigate to client dashboard
          navigate('/client-dashboard');
        }
      }

      // Alt + M: Navigate to Medications/Prescriptions
      if (event.altKey && event.key.toLowerCase() === 'm' && !isInputField) {
        event.preventDefault();

        if (location.pathname.includes('client-dashboard')) {
          // Navigate to prescriptions section
          window.dispatchEvent(new CustomEvent('navigate-to-section', {
            detail: { section: 'myPrescriptions' }
          }));
        } else if (location.pathname.includes('provider-dashboard')) {
          // Navigate to prescriptions tab
          window.dispatchEvent(new CustomEvent('navigate-to-tab', {
            detail: { tab: 'prescriptions' }
          }));
        } else {
          navigate('/client-dashboard');
        }
      }

      // Alt + H: Open Hope AI
      if (event.altKey && event.key.toLowerCase() === 'h' && !isInputField) {
        event.preventDefault();

        // Trigger Hope AI modal/button
        window.dispatchEvent(new CustomEvent('open-hope-ai'));
      }

      // Alt + S: Navigate to Settings
      if (event.altKey && event.key.toLowerCase() === 's' && !isInputField) {
        event.preventDefault();

        if (location.pathname.includes('provider-dashboard')) {
          window.dispatchEvent(new CustomEvent('navigate-to-tab', {
            detail: { tab: 'settings' }
          }));
        }
      }

      // Alt + C: Navigate to Clients (Provider only)
      if (event.altKey && event.key.toLowerCase() === 'c' && !isInputField) {
        event.preventDefault();

        if (location.pathname.includes('provider-dashboard')) {
          window.dispatchEvent(new CustomEvent('navigate-to-tab', {
            detail: { tab: 'clients' }
          }));
        }
      }

      // / key: Focus search (if search exists)
      if (event.key === '/' && !isInputField) {
        event.preventDefault();

        // Try to find and focus search input
        const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search" i], input[aria-label*="search" i]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }

      // Escape: Close modals/overlays
      if (event.key === 'Escape') {
        // Dispatch event for modals to listen to
        window.dispatchEvent(new CustomEvent('close-modals'));
      }

      // Alt + ?: Show keyboard shortcuts help
      if (event.altKey && (event.key === '?' || event.key === '/') && !isInputField) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent('show-keyboard-shortcuts-help'));
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, location]);

  // This component doesn't render anything
  return null;
};

export default KeyboardShortcuts;
